import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import './MedCurationLanding.css';
import {
  getHuggingFaceRedirectUri,
  HUGGING_FACE_RETURN_PATH_KEY,
  startHuggingFaceAuth,
} from './medCurationAuth';

const huggingFaceLogoUrl =
  'https://huggingface.co/front/assets/huggingface_logo-noborder.svg';

/**
 * Start the Hugging Face OAuth flow.
 *
 * Follows the LabelBricks server-driven pattern: ask the backend for an
 * authorization URL (passing the redirect URI this app is hosting), then send
 * the browser to Hugging Face. After consent, Hugging Face redirects back to
 * `/auth/huggingface/callback`, which finishes the exchange.
 */
async function beginHuggingFaceSignIn(): Promise<void> {
  const redirectUri = getHuggingFaceRedirectUri();

  try {
    // Remember to land in the viewer after a successful sign-in.
    localStorage.setItem(HUGGING_FACE_RETURN_PATH_KEY, '/');
  } catch {
    /* ignore storage failures */
  }

  const { authorizationUrl } = await startHuggingFaceAuth(redirectUri);
  window.location.assign(authorizationUrl);
}


const capabilities = [
  {
    id: '01',
    name: 'Study intake',
    copy: 'Pull DICOM and NIfTI studies from connected sources with metadata validation and duplicate detection before anything enters review.',
  },
  {
    id: '02',
    name: 'Cohort triage',
    copy: 'Sort, filter, and stage imaging studies into curated cohorts with modality, body-part, and quality signals surfaced up front.',
  },
  {
    id: '03',
    name: 'Review & markup',
    copy: 'Inspect series in the full OHIF viewer with measurements, segmentation review, and reviewer handoffs kept in one place.',
  },
  {
    id: '04',
    name: 'Export packages',
    copy: 'Produce review-ready dataset packages with lineage intact, ready for annotation, training, or downstream validation.',
  },
];

const flowSteps = [
  {
    step: '01',
    title: 'Connect',
    copy: 'Sign in with Hugging Face and open the studies backed by your account server.',
  },
  {
    step: '02',
    title: 'Curate',
    copy: 'Triage incoming studies into clean, decision-ready cohorts.',
  },
  {
    step: '03',
    title: 'Review',
    copy: 'Open series in the viewer and capture measurements and findings.',
  },
  {
    step: '04',
    title: 'Deliver',
    copy: 'Export governed dataset packages with full traceability.',
  },
];

const featureRows = [
  {
    kicker: 'Intake & Triage',
    title: 'Turn raw imaging into review-ready cohorts',
    copy: 'Bring studies in from your connected data source, validate structure and metadata, and stage them into curated cohorts — without leaving the viewer.',
    points: [
      'Metadata-validated DICOM & NIfTI intake',
      'Quality and modality signals up front',
      'Cohort staging with duplicate detection',
    ],
  },
  {
    kicker: 'Review & Governance',
    title: 'Inspect, measure, and keep every decision traceable',
    copy: 'Open studies in the full-featured OHIF viewer, capture measurements and segmentation review, and keep an auditable record tied to your account.',
    points: [
      'Full 2D / 3D imaging review',
      'Reviewer handoffs and findings capture',
      'Account-bound, server-backed storage',
    ],
  },
];

function MedCurationLanding() {
  const [authError, setAuthError] = React.useState<string | null>(null);
  const [signingIn, setSigningIn] = React.useState(false);

  const handleSignIn = useCallback(async () => {
    if (signingIn) {
      return;
    }
    setSigningIn(true);
    setAuthError(null);
    try {
      await beginHuggingFaceSignIn();
      // On success the browser navigates away; keep the button disabled.
    } catch (error) {
      setSigningIn(false);
      setAuthError(
        error instanceof Error
          ? error.message
          : 'Failed to start Hugging Face sign-in.'
      );
    }
  }, [signingIn]);

  return (
    <div className="mc-page">
      <header className="mc-nav">
        <div className="mc-container mc-nav-inner">
          <a
            className="mc-brand"
            href="/welcome"
            aria-label="Med-Curation home"
          >
            <span className="mc-brand-mark" aria-hidden="true">
              <svg
                viewBox="0 0 32 32"
                className="mc-brand-icon"
                role="img"
                aria-label="Med-Curation"
              >
                <rect
                  x="3"
                  y="3"
                  width="26"
                  height="26"
                  rx="7"
                  fill="url(#mcGrad)"
                />
                <path
                  d="M16 9v14M9 16h14"
                  stroke="#fff"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                />
                <defs>
                  <linearGradient
                    id="mcGrad"
                    x1="3"
                    y1="3"
                    x2="29"
                    y2="29"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#e11d48" />
                    <stop offset="1" stopColor="#9f1239" />
                  </linearGradient>
                </defs>
              </svg>
            </span>
            <span className="mc-brand-word">Med-Curation</span>
          </a>

          <nav
            className="mc-nav-links"
            aria-label="Primary"
          >
            <a href="#platform">Platform</a>
            <a href="#workflow">Workflow</a>
            <a href="#capabilities">Capabilities</a>
            <a href="#security">Security</a>
          </nav>

          <div className="mc-nav-actions">
            <Link
              className="mc-nav-viewer"
              to="/"
            >
              Open viewer
            </Link>
            <button
              type="button"
              className="mc-hf"
              onClick={handleSignIn}
              disabled={signingIn}
            >
              <img
                className="mc-hf-icon"
                src={huggingFaceLogoUrl}
                alt=""
                loading="lazy"
                decoding="async"
              />
              <span>{signingIn ? 'Redirecting…' : 'Sign in'}</span>
            </button>
          </div>
        </div>
      </header>

      <main className="mc-main">
        <section
          className="mc-hero"
          id="platform"
        >
          <div className="mc-container mc-hero-grid">
            <div className="mc-hero-copy">
              <span className="mc-eyebrow">
                Medical imaging curation · Powered by OHIF
              </span>
              <h1 className="mc-hero-title">
                Curate medical imaging datasets with confidence.
              </h1>
              <p className="mc-hero-lede">
                Med-Curation turns scattered studies into governed, review-ready
                datasets. Sign in with Hugging Face to open your studies, triage
                cohorts, and deliver traceable dataset packages — all inside a
                clinical-grade viewer.
              </p>
              <div className="mc-hero-cta">
                <button
                  type="button"
                  className="mc-btn mc-btn-hf"
                  onClick={handleSignIn}
                  disabled={signingIn}
                >
                  <img
                    className="mc-hf-icon"
                    src={huggingFaceLogoUrl}
                    alt=""
                    loading="lazy"
                    decoding="async"
                  />
                  <span>
                    {signingIn ? 'Redirecting…' : 'Sign in with Hugging Face'}
                  </span>
                </button>
                <Link
                  className="mc-btn mc-btn-ghost"
                  to="/"
                >
                  Open the viewer
                  <span
                    className="mc-btn-arrow"
                    aria-hidden="true"
                  >
                    →
                  </span>
                </Link>
              </div>
              {authError ? (
                <p
                  className="mc-hero-error"
                  role="alert"
                >
                  {authError}
                </p>
              ) : null}
              <p className="mc-hero-note">
                Uses your Hugging Face identity — no separate account needed.
              </p>
              <dl className="mc-hero-stats">
                <div>
                  <dt>DICOM · NIfTI</dt>
                  <dd>Validated intake</dd>
                </div>
                <div>
                  <dt>2D · 3D</dt>
                  <dd>Clinical review</dd>
                </div>
                <div>
                  <dt>Account-bound</dt>
                  <dd>Server-backed storage</dd>
                </div>
              </dl>
            </div>

            <div
              className="mc-hero-visual"
              aria-hidden="true"
            >
              <div className="mc-window">
                <div className="mc-window-bar">
                  <span className="mc-window-dot" />
                  <span className="mc-window-dot" />
                  <span className="mc-window-dot" />
                  <em className="mc-window-title">med-curation · review</em>
                </div>
                <div className="mc-window-body">
                  <div className="mc-window-viewport">
                    <span className="mc-scan mc-scan-a" />
                    <span className="mc-scan mc-scan-b" />
                    <span className="mc-crosshair" />
                  </div>
                  <div className="mc-window-side">
                    <span className="mc-side-row" />
                    <span className="mc-side-row" />
                    <span className="mc-side-row mc-side-row-active" />
                    <span className="mc-side-row" />
                    <span className="mc-side-row" />
                  </div>
                </div>
              </div>
              <div className="mc-hero-float mc-hero-float-a">
                <span className="mc-float-k">Cohort</span>
                <span className="mc-float-v">Intake → Export</span>
              </div>
              <div className="mc-hero-float mc-hero-float-b">
                <span className="mc-float-dot" />
                <span>Traceable review</span>
              </div>
            </div>
          </div>
        </section>

        <section className="mc-trust">
          <div className="mc-container mc-trust-inner">
            <span className="mc-trust-label">
              Built on the standards imaging teams already trust
            </span>
            <div className="mc-trust-logos">
              <span>OHIF</span>
              <span>DICOMweb</span>
              <span>Hugging Face</span>
              <span>NIfTI</span>
              <span>LabelBricks</span>
              <span>MONAI</span>
            </div>
          </div>
        </section>

        <section className="mc-statement">
          <div className="mc-container">
            <p className="mc-statement-text">
              Dataset curation shouldn't mean juggling exports and spreadsheets.
              <strong>
                {' '}
                Med-Curation gives teams one auditable surface
              </strong>{' '}
              to move from raw studies to review-ready, deliverable datasets.
            </p>
          </div>
        </section>

        <section className="mc-features">
          <div className="mc-container mc-features-inner">
            {featureRows.map((row, index) => (
              <div
                className={`mc-feature ${index % 2 === 1 ? 'mc-feature-rev' : ''}`}
                key={row.title}
              >
                <div className="mc-feature-text">
                  <span className="mc-eyebrow">{row.kicker}</span>
                  <h2 className="mc-feature-title">{row.title}</h2>
                  <p className="mc-feature-copy">{row.copy}</p>
                  <ul className="mc-checks">
                    {row.points.map(point => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                </div>
                <div
                  className="mc-feature-visual"
                  aria-hidden="true"
                >
                  <div className="mc-mock">
                    <div className="mc-mock-bar">
                      <span className="mc-mock-tab" />
                      <span className="mc-mock-tab" />
                    </div>
                    <div className="mc-mock-body">
                      <div className="mc-mock-row">
                        <span className="mc-mock-chip" />
                        <span className="mc-mock-line" />
                      </div>
                      <div className="mc-mock-row">
                        <span className="mc-mock-chip" />
                        <span className="mc-mock-line" />
                      </div>
                      <div className="mc-mock-row">
                        <span className="mc-mock-chip" />
                        <span className="mc-mock-line" />
                      </div>
                      <div className="mc-mock-row">
                        <span className="mc-mock-chip" />
                        <span className="mc-mock-line" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section
          className="mc-flow"
          id="workflow"
        >
          <div className="mc-container">
            <div className="mc-section-head">
              <span className="mc-eyebrow">How it works</span>
              <h2 className="mc-h2">
                A single path from intake to delivery
              </h2>
            </div>
            <ol className="mc-flow-track">
              {flowSteps.map(stage => (
                <li
                  className="mc-flow-step"
                  key={stage.step}
                >
                  <span className="mc-flow-num">{stage.step}</span>
                  <h3 className="mc-flow-title">{stage.title}</h3>
                  <p className="mc-flow-copy">{stage.copy}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section
          className="mc-modules"
          id="capabilities"
        >
          <div className="mc-container">
            <div className="mc-section-head mc-section-head-split">
              <div>
                <span className="mc-eyebrow">Capabilities</span>
                <h2 className="mc-h2">
                  Everything curation needs, in one workspace
                </h2>
              </div>
              <p className="mc-section-note">
                Each stage is a dedicated surface — intake, triage, review, and
                export — not dashboards bolted together.
              </p>
            </div>
            <div className="mc-module-list">
              {capabilities.map(module => (
                <article
                  className="mc-module"
                  key={module.id}
                >
                  <span className="mc-module-id">{module.id}</span>
                  <div className="mc-module-body">
                    <h3 className="mc-module-name">{module.name}</h3>
                    <p className="mc-module-copy">{module.copy}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section
          className="mc-band"
          id="security"
        >
          <div className="mc-container mc-band-grid">
            <div className="mc-band-lead">
              <span className="mc-eyebrow mc-eyebrow-invert">
                Security &amp; governance
              </span>
              <h2 className="mc-band-title">
                Enterprise controls, without the enterprise friction.
              </h2>
              <p className="mc-band-copy">
                Hugging Face sign-in, account-bound server storage, and
                persistent review records keep every curation decision
                auditable, reproducible, and ready for handoff.
              </p>
            </div>
            <ul className="mc-band-points">
              <li>
                <strong>Provider-aware auth</strong>
                <span>Hugging Face identity with named user context.</span>
              </li>
              <li>
                <strong>Account-bound storage</strong>
                <span>Data stays tied to your linked server.</span>
              </li>
              <li>
                <strong>Traceable review</strong>
                <span>Findings and measurements kept auditable.</span>
              </li>
              <li>
                <strong>Standards-based</strong>
                <span>DICOMweb and NIfTI throughout.</span>
              </li>
            </ul>
          </div>
        </section>

        <section className="mc-cta">
          <div className="mc-container mc-cta-inner">
            <h2 className="mc-cta-title">
              Bring your imaging curation into one workspace.
            </h2>
            <p className="mc-cta-copy">
              Sign in with Hugging Face and open your governed curation
              workspace in seconds.
            </p>
            <div className="mc-cta-actions">
              <button
                type="button"
                className="mc-btn mc-btn-hf"
                onClick={handleSignIn}
                disabled={signingIn}
              >
                <img
                  className="mc-hf-icon"
                  src={huggingFaceLogoUrl}
                  alt=""
                  loading="lazy"
                  decoding="async"
                />
                <span>
                  {signingIn ? 'Redirecting…' : 'Sign in with Hugging Face'}
                </span>
              </button>
              <Link
                className="mc-btn mc-btn-ghost"
                to="/"
              >
                Open the viewer
                <span
                  className="mc-btn-arrow"
                  aria-hidden="true"
                >
                  →
                </span>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="mc-footer">
        <div className="mc-container mc-footer-inner">
          <div className="mc-footer-brand">
            <span className="mc-brand-mark" aria-hidden="true">
              <svg
                viewBox="0 0 32 32"
                className="mc-brand-icon"
                role="img"
                aria-label="Med-Curation"
              >
                <rect
                  x="3"
                  y="3"
                  width="26"
                  height="26"
                  rx="7"
                  fill="url(#mcGradFooter)"
                />
                <path
                  d="M16 9v14M9 16h14"
                  stroke="#fff"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                />
                <defs>
                  <linearGradient
                    id="mcGradFooter"
                    x1="3"
                    y1="3"
                    x2="29"
                    y2="29"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#e11d48" />
                    <stop offset="1" stopColor="#9f1239" />
                  </linearGradient>
                </defs>
              </svg>
            </span>
            <div>
              <div className="mc-brand-word">Med-Curation</div>
              <p className="mc-footer-tag">
                Medical imaging dataset curation built on OHIF.
              </p>
            </div>
          </div>
          <div className="mc-footer-meta">
            <span>Part of the LabelBricks product suite</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default MedCurationLanding;
